import * as React from "react";
import { StyleSheet, Animated, Easing } from "react-native";
import { View } from "../Themed";
import Colors from "../../constants/Colors";
import Layout from "../../constants/Layout";
import GlobalContext from "../../context/Global";

export const ShimmerPlaceholder = ({ width, height, style }: any) => {
  const animatedValue = React.useRef(new Animated.Value(0)).current;

  React.useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(animatedValue, {
          toValue: 1,
          duration: 1000,
          easing: Easing.linear,
          useNativeDriver: true,
        }),
        Animated.timing(animatedValue, {
          toValue: 0,
          duration: 1000,
          easing: Easing.linear,
          useNativeDriver: true,
        }),
      ])
    ).start();
  }, [animatedValue]);

  const opacity = animatedValue.interpolate({
    inputRange: [0, 1],
    outputRange: [0.3, 0.7],
  });

  return (
    <Animated.View
      style={[
        {
          width,
          height,
          backgroundColor: Colors.nuetral_grey_400,
          borderRadius: 4,
          opacity,
        },
        style,
      ]}
    />
  );
};

export default function OpenHouseListItemShimmer() {
  const { isTablet } = React.useContext(GlobalContext);

  return (
    <View
      lightColor={Colors.base_colors_white}
      darkColor={Colors.nuetral_grey_700}
      style={[
        styles.container,
        {
          paddingHorizontal: isTablet ? 32 : 16,
        },
      ]}
    >
      <View style={styles.openHouseInfoContainer}>
        <View style={styles.addressDateContainer}>
          {/* Address shimmer */}
          <ShimmerPlaceholder width="80%" height={20} />
          {/* Date shimmer */}
          <ShimmerPlaceholder
            width="60%"
            height={16}
            style={{ marginTop: 4 }}
          />
        </View>
      </View>
      <View style={styles.iconContainer}>
        <ShimmerPlaceholder width={24} height={24} />
      </View>
    </View>
  );
}

export function OpenHouseListShimmer({ status }: { status: string }) {
  const { isTablet } = React.useContext(GlobalContext);

  const getTitle = () => {
    switch (status) {
      case "upcoming":
        return "Upcoming";
      case "active":
        return "Current";
      case "completed":
        return "Completed";
      default:
        return status;
    }
  };

  return (
    <View
      style={[
        styles.listContainer,
        {
          marginTop: status === "current" ? 0 : 24,
        },
      ]}
    >
      <View style={styles.section}>
        <View
          style={[
            styles.sectionHeader,
            {
              paddingHorizontal: isTablet ? 32 : 16,
            },
          ]}
        >
          <ShimmerPlaceholder width={120} height={24} />
        </View>
        <OpenHouseListItemShimmer />
        <OpenHouseListItemShimmer />
      </View>
    </View>
  );
}

export function StatSectionShimmer() {
  return (
    <View style={styles.statsContainer}>
      <View style={{ gap: 8 }}>
        <ShimmerPlaceholder width={120} height={24} />
        <ShimmerPlaceholder width="100%" height={48} style={{ marginTop: 8 }} />
      </View>
      <View style={{ flexDirection: "row", marginTop: 16 }}>
        <View style={styles.statBlockShimmer}>
          <ShimmerPlaceholder width={60} height={36} />
          <ShimmerPlaceholder
            width={100}
            height={20}
            style={{ marginTop: 4 }}
          />
        </View>
        <View style={styles.statBlockShimmer}>
          <ShimmerPlaceholder width={60} height={36} />
          <ShimmerPlaceholder
            width={100}
            height={20}
            style={{ marginTop: 4 }}
          />
        </View>
        <View style={styles.statBlockShimmer}>
          <ShimmerPlaceholder width={60} height={36} />
          <ShimmerPlaceholder
            width={100}
            height={20}
            style={{ marginTop: 4 }}
          />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    gap: 24,
    paddingVertical: 16,
  },
  openHouseInfoContainer: {
    flex: 1,
    gap: 8,
    flexDirection: Layout.isLargeDevice ? "row" : "column",
    alignItems: Layout.isLargeDevice ? "center" : "flex-start",
    justifyContent: "space-between",
  },
  addressDateContainer: {
    gap: 8,
  },
  iconContainer: {
    alignItems: "center",
    justifyContent: "center",
  },
  listContainer: {
    gap: 24,
  },
  section: {
    gap: 8,
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  statsContainer: {
    padding: 16,
    backgroundColor: "transparent",
    zIndex: 9,
  },
  statBlockShimmer: {
    width: "33%",
    alignItems: "center",
    marginVertical: 8,
  },
});
